import { Injectable } from '@angular/core';

//
// Matomo tracking, on top of the Friendly Analytics tag-manager container inlined in index.html.
//
// The container carries exactly one Matomo tag: a page view on the PageView trigger. Without this
// service the entire visit is therefore a single action at /menu.html - which is not merely thin,
// it is degenerate. Matomo derives visit duration from the interval between actions, so one action
// means zero seconds and a bounce for every visitor, no matter how long somebody worked in the
// application. Goals, funnels and heatmaps all key on actions as well, so they stay empty for the
// same reason.
//
// This application has no Angular router (ai/ARCHITECTURE.md): navigation is service state plus the
// URL hash, and the in-app back link deliberately does not reload the document. There is thus no
// navigation event to hang tracking on, and every call below sits by hand at the point where
// something actually happened.
//
// Everything goes through `window._paq`, the queue Matomo's own tracker drains. That works both
// before and after the container has loaded: `Piwik.addTracker()` creates the tracker, applies this
// queue to it in order, and only then replaces `_paq` with the live proxy - so a call made while
// the container is still in flight is applied to the right tracker rather than lost. Verified
// against the delivered matomo.js, not assumed.
//
@Injectable({ providedIn: 'root' })
export class MatomoService
{
  //
  // Brave's shields and the ordinary blockers stop the container from ever loading, and nothing then
  // drains the queue: it would grow for as long as the tab stays open. Matomo swaps the plain array
  // for its own proxy object the moment a tracker is registered, so "still an Array" is exactly the
  // signal that no tracker ever arrived. The cap is generous enough that a real session never meets
  // it before the container has loaded.
  //
  private static readonly MAX_QUEUED_CALLS = 100;

  //
  // The previously reported virtual URL, handed to Matomo as the referrer of the next one. Without
  // it every virtual page view looks like a fresh entry from nowhere and the Transitions and
  // Behaviour-flow reports cannot connect two screens of the same visit.
  //
  private previousUrl: string | null = null;

  private push(call: unknown[]): void
  {
    const queue = window._paq = window._paq || [];

    if (Array.isArray(queue) && queue.length >= MatomoService.MAX_QUEUED_CALLS)
    {
      return;
    }

    queue.push(call);
  }

  //
  // A screen change reported as a page view of its own. `path` is an application-defined path rather
  // than the address bar's: the bookmark hash this application writes carries every parameter value
  // of the form (HashStateService.writeBookmark), which would put customer data - division codes,
  // dates, references - into the Pages report and give every download its own URL. What Matomo gets
  // is the report identity and nothing else.
  //
  // enableLinkTracking is repeated on purpose. Matomo binds its click handlers to the <a> elements
  // that exist when it runs, and this application renders its report links only after the catalogue
  // request returns, so the links present at the container's own page view are not the ones a user
  // clicks. Matomo marks the elements it has already bound, so repeating this does not double-count.
  //
  trackVirtualPageView(path: string, title: string): void
  {
    const url = new URL(path, document.baseURI).href;

    if (this.previousUrl)
    {
      this.push(['setReferrerUrl', this.previousUrl]);
    }

    this.push(['setCustomUrl', url]);
    this.push(['setDocumentTitle', title]);
    this.push(['trackPageView']);
    this.push(['enableLinkTracking']);

    this.previousUrl = url;
  }

  //
  // The visitor's identity, so that Matomo can link the visits of one person across sessions and
  // devices. It is the only way to get that here: the tracker Friendly Analytics delivers has the
  // visitor-cookie machinery removed - neither the bundled tracker nor matomo.js contains `_pk_id` or
  // `_pk_ses` at all - so there is no client-side identifier and the server-side heuristic recognises
  // nobody beyond the current visit. Measured over August 2026: 3,088 visits against 2,966 unique
  // visitors, i.e. 96% of visits counted as a new person.
  //
  // A User ID does not need a cookie. It travels as a request parameter and Matomo links the visits
  // server-side, which is precisely why it works where a cookie would have been required.
  //
  // Only ever called with the `IWID-…` form - see ThirdPartyIntegrationsService.initMatomo for why
  // that guard is not optional.
  //
  // The ping is what actually delivers it. Matomo sends a request per action, and by the time the
  // identity is known - it arrives in the catalogue XHR - the container has already sent this visit's
  // page view, without a `uid`. So setting the property alone leaves the identity sitting in the browser
  // until the next request happens to go out: the 15-second heartbeat, or a virtual page view when a
  // report is opened. A visit shorter than that, with no report opened, would stay anonymous.
  //
  // `ping=1` is Matomo's keep-alive: it carries the full parameter set, including `uid`, and does NOT
  // count as an action, so nothing is double-counted. Measured on production 2026-08-14: it was exactly
  // such a ping that first carried `uid=IWID-…`, fifteen seconds in. `ping` is a real method on the
  // delivered tracker (checked, unlike `trackPing`, which does not exist).
  //
  setUserId(userId: string): void
  {
    this.push(['setUserId', userId]);
    this.push(['ping']);
  }

  trackEvent(category: string, action: string, name?: string): void
  {
    this.push(name === undefined ? ['trackEvent', category, action] : ['trackEvent', category, action, name]);
  }

  //
  // A download reported the way Matomo reports one, so it lands in Behaviour > Downloads and can
  // carry a goal. The automatic detection cannot see these: it hooks link clicks, and a report is
  // fetched from JavaScript or submitted natively into a hidden iframe (DownloadService), never
  // through an <a href>. The URL passed in must be sanitised by the caller for the same reason as
  // above - the real request URL carries the parameter values.
  //
  trackDownload(url: string): void
  {
    this.push(['trackLink', url, 'download']);
  }

  //
  // Form Analytics is enabled in the container's Matomo configuration, but its scan runs once, at the
  // container's own page view. The parameter form does not exist yet at that moment - it is rendered
  // only when a report is opened - so without this call the Forms report stays empty for every report
  // in the catalogue. Dispatched through the plugin namespace, which Matomo re-tries once the plugin
  // itself has loaded if it has not yet.
  //
  scanForForms(): void
  {
    this.push(['FormAnalytics::scanForForms']);
  }
}

//
// The URL a download is reported under, which is deliberately not the URL that was requested. The
// real one carries every parameter value the user filled in - division codes, dates, references,
// whatever a report takes - so reporting it would put customer data in Behaviour > Downloads and
// give each individual download a URL of its own, leaving a report that lists thousands of rows and
// groups nothing. What remains identifies the report and nothing else.
//
export function sanitisedDownloadUrl(presetName: string): string
{
  return new URL('Preset?preset=' + encodeURIComponent(presetName), document.baseURI).href;
}
